import { useState, useMemo } from 'react';
import { formatCurrency, parseOccSymbol, toLocalDate } from '@/lib/formatters';
import { cn } from '@/lib/utils';
import type { PositionWithLegs } from '@steward/shared';
import { classifyPositionEvent } from '@steward/shared';
import type { PositionWithTransactions, LegWithTransactions } from '@/lib/buildPositionEvents';

interface ChainJourneyProps {
  positions: PositionWithLegs[];
}

interface JourneyStep {
  id: string;
  type: 'opened' | 'rolled' | 'expired' | 'closed';
  label: string;
  strategyLabel: string | null;
  date: Date;
  endDate: Date | null;
  status: 'open' | 'closed';
  isCurrent: boolean;
  daysToExpiration: number | null;
  closingLegs: JourneyLeg[];
  openingLegs: JourneyLeg[];
  netAmount: number;
  runningTotal: number;
  amountLabel: string;
}

interface JourneyLeg {
  action: string;
  quantity: number;
  optionType: 'call' | 'put';
  strikePrice: number;
  expLabel: string;
  pricePerContract: number;
  dollarAmount: number;
}

function detectStrategy(legs: Array<{ optionType: string; action: string }>): string {
  const puts = legs.filter(l => l.optionType === 'put');
  const calls = legs.filter(l => l.optionType === 'call');
  if (legs.length === 4 && puts.length === 2 && calls.length === 2) return 'Iron Condor';
  if (legs.length === 2) {
    if (puts.length === 2) return 'Put Spread';
    if (calls.length === 2) return 'Call Spread';
  }
  if (legs.length === 1) {
    const leg = legs[0];
    if (leg.action === 'STO' && leg.optionType === 'put') return 'Cash Secured Put';
    if (leg.action === 'STO' && leg.optionType === 'call') return 'Covered Call';
    return leg.optionType === 'put' ? 'Long Put' : 'Long Call';
  }
  return 'Custom';
}

function getRolledSide(legs: Array<{ optionType: string }>): string {
  const puts = legs.filter(l => l.optionType === 'put');
  const calls = legs.filter(l => l.optionType === 'call');
  if (puts.length > 0 && calls.length === 0) return 'Puts';
  if (calls.length > 0 && puts.length === 0) return 'Calls';
  return 'Position';
}

function fmtExp(dateInput: Date | string | number): string {
  const d = toLocalDate(dateInput);
  if (isNaN(d.getTime())) return '';
  return `${d.getMonth() + 1}/${d.getDate()}`;
}

function daysToExp(expDate: Date | string | number): number {
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  const exp = toLocalDate(expDate);
  exp.setHours(0, 0, 0, 0);
  return Math.max(0, Math.ceil((exp.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)));
}

function fmtDate(date: Date): string {
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function buildLeg(leg: { optionSymbol: string; action: string; quantity: number; optionType: string; strikePrice: number; expirationDate: Date | string | number; pricePerContract: number }, actionOverride?: string): JourneyLeg {
  const parsed = parseOccSymbol(leg.optionSymbol);
  const action = actionOverride || leg.action;
  return {
    action,
    quantity: leg.quantity,
    optionType: (parsed?.type || leg.optionType) as 'call' | 'put',
    strikePrice: parsed?.strike || leg.strikePrice,
    expLabel: parsed ? fmtExp(parsed.expiration) : fmtExp(leg.expirationDate),
    pricePerContract: leg.pricePerContract,
    dollarAmount: leg.pricePerContract * leg.quantity * 100 * (action === 'STO' || action === 'STC' ? 1 : -1),
  };
}

function buildJourneySteps(positions: PositionWithLegs[]): JourneyStep[] {
  const sorted = [...positions].sort((a, b) => a.rollNumber - b.rollNumber);
  const steps: JourneyStep[] = [];
  let runningTotal = 0;

  for (let i = 0; i < sorted.length; i++) {
    const pos = sorted[i];
    const isFirst = i === 0;
    const isCurrent = pos.status === 'open';

    const posOpeningLegs: JourneyLeg[] = pos.legs.map(leg => buildLeg(leg));
    const strategy = detectStrategy(posOpeningLegs);
    const side = getRolledSide(posOpeningLegs);
    const openingNet = posOpeningLegs.reduce((sum, l) => sum + l.dollarAmount, 0);

    if (isFirst) {
      runningTotal += openingNet;
      steps.push({
        id: `${pos.id}-open`,
        type: 'opened',
        label: `Opened ${strategy}`,
        strategyLabel: strategy,
        date: new Date(pos.openedAt),
        endDate: pos.closedAt ? new Date(pos.closedAt) : null,
        status: pos.status as 'open' | 'closed',
        isCurrent,
        daysToExpiration: isCurrent ? daysToExp(pos.legs[0]?.expirationDate) : null,
        closingLegs: [],
        openingLegs: posOpeningLegs,
        netAmount: openingNet,
        runningTotal,
        amountLabel: openingNet >= 0 ? 'Opening Credit' : 'Opening Debit',
      });
    } else {
      // Classify roll vs. fresh open from the predecessor's terminal state
      // (expired/assigned/exercised → fresh open), not rollNumber. See
      // @steward/shared classifyPositionEvent.
      const prevPos = sorted[i - 1] as PositionWithTransactions;
      const isRoll = classifyPositionEvent(pos, sorted[i - 1] ?? null) === 'rolled';

      if (isRoll) {
        // Roll: close previous + open new
        let closingCost = 0;
        const rollClosingLegs: JourneyLeg[] = [];

        if (prevPos.legs) {
          for (const leg of prevPos.legs) {
            const closeTxns = (leg as LegWithTransactions).transactions?.filter(
              t => t.transactionType === 'close' && t.pricePerContract > 0
            ) || [];
            for (const txn of closeTxns) {
              const isClosingDebit = leg.action === 'STO' || leg.action === 'STC';
              const closingAction = isClosingDebit ? 'BTC' : 'STC';
              const jLeg = buildLeg(leg, closingAction);
              // Override with actual close price
              const amount = txn.pricePerContract * txn.quantity * 100 * (isClosingDebit ? -1 : 1);
              jLeg.pricePerContract = txn.pricePerContract;
              jLeg.dollarAmount = amount;
              jLeg.quantity = txn.quantity;
              closingCost += amount;
              rollClosingLegs.push(jLeg);
            }
          }
        }

        const rollNet = closingCost + openingNet;
        runningTotal += rollNet;

        steps.push({
          id: `${pos.id}-roll`,
          type: 'rolled',
          label: `Rolled ${side}`,
          strategyLabel: strategy,
          date: new Date(pos.openedAt),
          endDate: pos.closedAt ? new Date(pos.closedAt) : null,
          status: pos.status as 'open' | 'closed',
          isCurrent,
          daysToExpiration: isCurrent ? daysToExp(pos.legs[0]?.expirationDate) : null,
          closingLegs: rollClosingLegs,
          openingLegs: posOpeningLegs,
          netAmount: rollNet,
          runningTotal,
          amountLabel: rollNet >= 0 ? 'Roll Credit' : 'Roll Debit',
        });
      } else {
        // Parallel position (e.g., short call written against open LEAPS)
        runningTotal += openingNet;
        steps.push({
          id: `${pos.id}-open`,
          type: 'opened',
          label: `Opened ${strategy}`,
          strategyLabel: strategy,
          date: new Date(pos.openedAt),
          endDate: pos.closedAt ? new Date(pos.closedAt) : null,
          status: pos.status as 'open' | 'closed',
          isCurrent,
          daysToExpiration: isCurrent ? daysToExp(pos.legs[0]?.expirationDate) : null,
          closingLegs: [],
          openingLegs: posOpeningLegs,
          netAmount: openingNet,
          runningTotal,
          amountLabel: openingNet >= 0 ? 'Opening Credit' : 'Opening Debit',
        });
      }
    }

    // Show closing events for closed positions (unless the next position is a roll,
    // which already includes the closing cost in its step)
    if (pos.status === 'closed') {
      const expiredLegs = pos.legs.filter(l => l.closeReason === 'expired');
      const assignedLegs = pos.legs.filter(l => l.closeReason === 'assigned');
      const exercisedLegs = pos.legs.filter(l => l.closeReason === 'exercised');
      const closedLegs = pos.legs.filter(l => l.closeReason === 'closed');
      const nextPos = sorted[i + 1];
      const nextOpenedAt = nextPos ? new Date(nextPos.openedAt) : null;
      const posClosedAt = pos.closedAt ? new Date(pos.closedAt) : null;
      const isNextARoll = nextPos && posClosedAt && nextOpenedAt && posClosedAt <= nextOpenedAt;

      // Assigned legs — show assignment event with stock buy/sell
      if (assignedLegs.length > 0 && !isNextARoll) {
        const assignJourneyLegs: JourneyLeg[] = assignedLegs.map(leg => {
          const jLeg = buildLeg(leg);
          jLeg.action = '';
          jLeg.dollarAmount = 0;
          return jLeg;
        });

        // Find stock transactions from assigned legs
        let stockLabel = '';
        for (const leg of assignedLegs) {
          const txns = (leg as LegWithTransactions).transactions || [];
          const stockTxn = txns.find(t => t.transactionType === 'stock_buy' || t.transactionType === 'stock_sell');
          if (stockTxn) {
            const action = stockTxn.transactionType === 'stock_buy' ? 'Bought' : 'Sold';
            stockLabel += `${action} ${stockTxn.quantity} shares @ ${formatCurrency(stockTxn.pricePerContract)}. `;
          }
        }

        steps.push({
          id: `${pos.id}-assigned`,
          type: 'closed',
          label: `Assigned${stockLabel ? '' : ''}`,
          strategyLabel: null,
          date: posClosedAt || new Date(pos.openedAt),
          endDate: null,
          status: 'closed',
          isCurrent: false,
          daysToExpiration: null,
          closingLegs: [],
          openingLegs: assignJourneyLegs,
          netAmount: 0,
          runningTotal,
          amountLabel: stockLabel.trim(),
        });
      }

      // Exercised legs — show exercise event with stock buy/sell
      if (exercisedLegs.length > 0 && !isNextARoll) {
        const exerciseJourneyLegs: JourneyLeg[] = exercisedLegs.map(leg => {
          const jLeg = buildLeg(leg);
          jLeg.action = '';
          jLeg.dollarAmount = 0;
          return jLeg;
        });

        let stockLabel = '';
        for (const leg of exercisedLegs) {
          const txns = (leg as LegWithTransactions).transactions || [];
          const stockTxn = txns.find(t => t.transactionType === 'stock_buy' || t.transactionType === 'stock_sell');
          if (stockTxn) {
            const action = stockTxn.transactionType === 'stock_buy' ? 'Bought' : 'Sold';
            stockLabel += `${action} ${stockTxn.quantity} shares @ ${formatCurrency(stockTxn.pricePerContract)}. `;
          }
        }

        steps.push({
          id: `${pos.id}-exercised`,
          type: 'closed',
          label: `Exercised`,
          strategyLabel: null,
          date: posClosedAt || new Date(pos.openedAt),
          endDate: null,
          status: 'closed',
          isCurrent: false,
          daysToExpiration: null,
          closingLegs: [],
          openingLegs: exerciseJourneyLegs,
          netAmount: 0,
          runningTotal,
          amountLabel: stockLabel.trim(),
        });
      }

      if (expiredLegs.length > 0 && !isNextARoll) {
        // Expired event (all or partial expiry, unless next position is a roll)
        const expJourneyLegs: JourneyLeg[] = expiredLegs.map(leg => {
          const jLeg = buildLeg(leg);
          jLeg.action = ''; // Don't show BTO/STO for expired legs
          jLeg.dollarAmount = 0;
          return jLeg;
        });

        const expSide = getRolledSide(expJourneyLegs);
        const premiumKept = expiredLegs.reduce((sum, leg) => {
          const value = leg.pricePerContract * leg.quantity * 100;
          return sum + (leg.action === 'STO' || leg.action === 'STC' ? value : -value);
        }, 0);

        // Use the actual expiration date from the legs, not pos.closedAt
        const expirationDate = toLocalDate(Math.max(
          ...expiredLegs.map(l => toLocalDate(l.expirationDate).getTime())
        ));

        steps.push({
          id: `${pos.id}-expired`,
          type: 'expired',
          label: `${expSide} Expired Worthless`,
          strategyLabel: null,
          date: expirationDate,
          endDate: null,
          status: 'closed',
          isCurrent: false,
          daysToExpiration: null,
          closingLegs: [],
          openingLegs: expJourneyLegs,
          netAmount: 0, // No new cash flow — premium was counted at opening
          runningTotal,
          amountLabel: premiumKept > 0 ? `Premium kept: ${formatCurrency(premiumKept, { showSign: true })}` : '',
        });
      } else if (closedLegs.length > 0 && !isNextARoll) {
        // Closed event (BTC/STC, unless next position is a roll which already shows the close)
        const closeJourneyLegs: JourneyLeg[] = [];
        let closingCost = 0;

        for (const leg of closedLegs) {
          const closeTxns = (leg as LegWithTransactions).transactions?.filter(
            t => t.transactionType === 'close' && t.pricePerContract > 0
          ) || [];
          for (const txn of closeTxns) {
            const isClosingDebit = leg.action === 'STO' || leg.action === 'STC';
            const closingAction = isClosingDebit ? 'BTC' : 'STC';
            const jLeg = buildLeg(leg, closingAction);
            const amount = txn.pricePerContract * txn.quantity * 100 * (isClosingDebit ? -1 : 1);
            jLeg.pricePerContract = txn.pricePerContract;
            jLeg.dollarAmount = amount;
            jLeg.quantity = txn.quantity;
            closingCost += amount;
            closeJourneyLegs.push(jLeg);
          }
        }

        if (closeJourneyLegs.length > 0) {
          runningTotal += closingCost;
          const side = getRolledSide(closeJourneyLegs);
          steps.push({
            id: `${pos.id}-closed`,
            type: 'closed',
            label: `Closed ${side}`,
            strategyLabel: null,
            date: posClosedAt || new Date(pos.openedAt),
            endDate: null,
            status: 'closed',
            isCurrent: false,
            daysToExpiration: null,
            closingLegs: closeJourneyLegs,
            openingLegs: [],
            netAmount: closingCost,
            runningTotal,
            amountLabel: closingCost >= 0 ? 'Closing Credit' : 'Closing Debit',
          });
        }
      }
    }
  }

  // Sort chronologically, then reverse for display (newest first)
  steps.sort((a, b) => a.date.getTime() - b.date.getTime());

  // Recalculate running totals after sort
  let total = 0;
  for (const step of steps) {
    if (step.type !== 'expired') {
      total += step.netAmount;
    }
    step.runningTotal = total;
  }

  return steps.reverse();
}

// ── Rendering ──

function LegLine({ leg, showFills }: { leg: JourneyLeg; showFills: boolean }) {
  const typeLabel = leg.optionType === 'put' ? 'P' : 'C';
  const strike = leg.strikePrice % 1 === 0 ? leg.strikePrice.toString() : leg.strikePrice.toFixed(1);

  return (
    <div className="flex items-center gap-1.5 text-[13px] leading-5">
      {leg.action ? (
        <span className={cn(
          'font-mono font-medium w-7',
          (leg.action === 'STO' || leg.action === 'STC') ? 'text-green-700' : 'text-red-600'
        )}>
          {leg.action}
        </span>
      ) : (
        <span className="w-7" />
      )}
      <span className="text-gray-500 w-7 text-right">{leg.quantity}x</span>
      <span className={cn(
        'font-semibold w-12',
        leg.optionType === 'put' ? 'text-red-500' : 'text-blue-500'
      )}>
        {strike}{typeLabel}
      </span>
      <span className="text-gray-400 w-10">{leg.expLabel}</span>
      {showFills && (
        <>
          <span className="text-gray-400">@${leg.pricePerContract.toFixed(2)}</span>
          <span className={cn(
            'ml-auto tabular-nums font-medium',
            leg.dollarAmount >= 0 ? 'text-green-600' : 'text-red-600'
          )}>
            {formatCurrency(leg.dollarAmount, { showSign: true })}
          </span>
        </>
      )}
    </div>
  );
}

function RollComparison({ closingLegs, openingLegs, showFills }: {
  closingLegs: JourneyLeg[];
  openingLegs: JourneyLeg[];
  showFills: boolean;
}) {
  return (
    <div className="grid grid-cols-2 gap-2">
      <div>
        <div className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-0.5">Closed</div>
        {closingLegs.map((leg, i) => <LegLine key={i} leg={leg} showFills={showFills} />)}
      </div>
      <div>
        <div className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-0.5">Opened</div>
        {openingLegs.map((leg, i) => <LegLine key={i} leg={leg} showFills={showFills} />)}
      </div>
    </div>
  );
}

function StepIcon({ type }: { type: JourneyStep['type'] }) {
  const base = 'w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0';
  const iconCls = 'w-3 h-3';
  switch (type) {
    case 'opened':
      return (
        <div className={cn(base, 'bg-green-100 text-green-600')}>
          <svg className={iconCls} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" />
          </svg>
        </div>
      );
    case 'rolled':
      return (
        <div className={cn(base, 'bg-blue-100 text-blue-600')}>
          <svg className={iconCls} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
        </div>
      );
    case 'expired':
      return (
        <div className={cn(base, 'bg-gray-100 text-gray-500')}>
          <svg className={iconCls} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
      );
    case 'closed':
      return (
        <div className={cn(base, 'bg-gray-100 text-gray-600')}>
          <svg className={iconCls} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
          </svg>
        </div>
      );
  }
}

export function ChainJourney({ positions }: ChainJourneyProps) {
  const [showDetails, setShowDetails] = useState(false);
  const [showFills, setShowFills] = useState(false);

  const steps = useMemo(() => buildJourneySteps(positions), [positions]);

  if (steps.length === 0) return null;

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900">Chain Journey</h3>
        <div className="flex items-center gap-4">
          {showDetails && (
            <label className="flex items-center gap-1.5 text-xs text-gray-500 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={showFills}
                onChange={(e) => setShowFills(e.target.checked)}
                className="rounded border-gray-300 text-brand-600 focus:ring-brand-500 h-3.5 w-3.5"
              />
              Fill prices
            </label>
          )}
          <label className="flex items-center gap-1.5 text-xs text-gray-500 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={showDetails}
              onChange={(e) => setShowDetails(e.target.checked)}
              className="rounded border-gray-300 text-brand-600 focus:ring-brand-500 h-3.5 w-3.5"
            />
            Show legs
          </label>
        </div>
      </div>

      <div className="relative">
        <div className="absolute left-3 top-6 bottom-6 w-px bg-gray-200" />

        <div className="space-y-0">
          {steps.map((step, idx) => (
            <div key={step.id} className="relative flex gap-3">
              <div className="relative z-10 pt-0.5">
                <StepIcon type={step.type} />
              </div>

              <div className={cn(
                'flex-1 min-w-0 pb-3',
                idx === steps.length - 1 && 'pb-0'
              )}>
                {/* Header line: label, date, badges, amount — all one row */}
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-semibold text-gray-900 text-sm">{step.label}</span>
                  <span className="text-xs text-gray-400">{fmtDate(step.date)}</span>
                  {step.isCurrent && (
                    <span className="text-[11px] px-1.5 py-0.5 bg-green-100 text-green-700 rounded font-medium">
                      Open
                    </span>
                  )}
                  {step.isCurrent && step.daysToExpiration !== null && (
                    <span className={cn(
                      'text-[11px] px-1.5 py-0.5 rounded font-medium',
                      step.daysToExpiration <= 5 ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-500'
                    )}>
                      {step.daysToExpiration} DTE
                    </span>
                  )}
                  {/* Amount on the right */}
                  <span className="ml-auto flex items-center gap-2">
                    {step.type === 'expired' ? (
                      <span className="text-xs text-gray-400 italic">{step.amountLabel}</span>
                    ) : (
                      <span className={cn(
                        'text-xs font-semibold',
                        step.netAmount >= 0 ? 'text-green-600' : 'text-red-600'
                      )}>
                        {formatCurrency(step.netAmount, { showSign: true })}
                      </span>
                    )}
                    {idx < steps.length - 1 && (
                      <span className="text-[11px] text-gray-400">
                        Total: {formatCurrency(step.runningTotal, { showSign: true })}
                      </span>
                    )}
                  </span>
                </div>

                {/* Expanded leg details */}
                {showDetails && (
                  <div className="mt-1">
                    {step.type === 'rolled' && step.closingLegs.length > 0 ? (
                      <RollComparison
                        closingLegs={step.closingLegs}
                        openingLegs={step.openingLegs}
                        showFills={showFills}
                      />
                    ) : step.type === 'closed' && step.closingLegs.length > 0 ? (
                      <div className="space-y-0">
                        {step.closingLegs.map((leg, legIdx) => (
                          <LegLine key={legIdx} leg={leg} showFills={showFills} />
                        ))}
                      </div>
                    ) : (
                      <div className="space-y-0">
                        {step.openingLegs.map((leg, legIdx) => (
                          <LegLine key={legIdx} leg={leg} showFills={showFills} />
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
